import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import instance from "../../api/api";
import { useTenant } from "../../context/Tenant/TenantContext";

const ENDPOINT = "/api/admin/risk-config/perspectives";

// ── Helpers ────────────────────────────────────────────────────────────────────

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function toPayload(perspectives) {
  return {
    Perspective: perspectives.map((p) => p.name.trim()),
    Factors: perspectives.flatMap((p) =>
      p.objectives.map((o) => ({ [p.name.trim()]: o.text.trim() }))
    ),
  };
}

// First problem the backend would reject, so Save can explain it up front.
function findProblem(perspectives) {
  if (perspectives.length === 0) return "Add at least one perspective.";
  const names = new Set();
  for (const p of perspectives) {
    const name = p.name.trim();
    if (!name) return "Every perspective needs a name.";
    if (names.has(name.toLowerCase())) return `"${name}" is listed twice.`;
    names.add(name.toLowerCase());
    if (p.objectives.length === 0) return `"${name}" needs at least one objective.`;
    const objectives = new Set();
    for (const o of p.objectives) {
      const text = o.text.trim();
      if (!text) return `An objective under "${name}" is empty.`;
      if (objectives.has(text.toLowerCase())) return `"${text}" is listed twice under "${name}".`;
      objectives.add(text.toLowerCase());
    }
  }
  return null;
}

// ── Usage pill ─────────────────────────────────────────────────────────────────

function UsagePill({ count }) {
  if (!count) return null;
  return (
    <span
      className="flex-shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500"
      title="Existing risks keep this text even if you rename or remove it"
    >
      Used by {plural(count, "risk")}
    </span>
  );
}

const IconRemove = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const INPUT =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-caribbeanCurrent focus:outline-none focus:ring-1 focus:ring-caribbeanCurrent";

// ── Main ───────────────────────────────────────────────────────────────────────

export default function PerspectiveSettings() {
  const { updateConfig } = useTenant();
  const keyRef = useRef(0);
  const newKey = () => `k${keyRef.current++}`;

  const [perspectives, setPerspectives] = useState([]);
  const [saved, setSaved] = useState("");
  const [usage, setUsage] = useState({ perspectives: {}, objectives: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmImpact, setConfirmImpact] = useState(null);
  const [notice, setNotice] = useState(null);
  // Newly added inputs take focus so the admin can type straight away
  const [focusKey, setFocusKey] = useState(null);

  const applyServerData = useCallback((data) => {
    const factors = data.Factors || [];
    const list = (data.Perspective || []).map((name) => ({
      key: `k${keyRef.current++}`,
      name,
      originalName: name,
      objectives: factors
        .filter((f) => Object.prototype.hasOwnProperty.call(f, name))
        .map((f) => ({ key: `k${keyRef.current++}`, text: f[name], originalText: f[name] })),
    }));
    setPerspectives(list);
    setSaved(JSON.stringify(toPayload(list)));
    setUsage(data.usage || { perspectives: {}, objectives: [] });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await instance.get(ENDPOINT);
      applyServerData(res.data.data);
    } catch (e) {
      setLoadError(e?.response?.data?.message ?? "Could not load perspectives and objectives.");
    } finally {
      setLoading(false);
    }
  }, [applyServerData]);

  useEffect(() => { load(); }, [load]);

  const objectiveUsage = (perspective, objective) =>
    usage.objectives.find((u) => u.perspective === perspective && u.objective === objective)?.count || 0;

  const payload = useMemo(() => toPayload(perspectives), [perspectives]);
  const dirty = !loading && JSON.stringify(payload) !== saved;
  const problem = findProblem(perspectives);

  // Renamed or removed items that existing risks still reference.
  const impact = useMemo(() => {
    const changes = [];
    const savedPayload = saved ? JSON.parse(saved) : { Perspective: [], Factors: [] };
    savedPayload.Perspective.forEach((name) => {
      const count = usage.perspectives[name] || 0;
      const current = perspectives.find((p) => p.originalName === name);
      if (!current) {
        if (count) changes.push(`Perspective "${name}" is removed (${plural(count, "risk")} use it).`);
        return;
      }
      if (current.name.trim() !== name) {
        if (count) changes.push(`Perspective "${name}" is renamed to "${current.name.trim()}" (${plural(count, "risk")} use it).`);
        return;
      }
      savedPayload.Factors.filter((f) => Object.prototype.hasOwnProperty.call(f, name)).forEach((f) => {
        const text = f[name];
        const used = objectiveUsage(name, text);
        if (!used) return;
        const o = current.objectives.find((x) => x.originalText === text);
        if (!o) changes.push(`Objective "${text}" is removed from "${name}" (${plural(used, "risk")} use it).`);
        else if (o.text.trim() !== text) changes.push(`Objective "${text}" is renamed to "${o.text.trim()}" (${plural(used, "risk")} use it).`);
      });
    });
    return changes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perspectives, saved, usage]);

  // ── Editing ──

  const updatePerspective = (key, change) =>
    setPerspectives((list) => list.map((p) => (p.key === key ? { ...p, ...change } : p)));

  const updateObjectives = (key, fn) =>
    setPerspectives((list) => list.map((p) => (p.key === key ? { ...p, objectives: fn(p.objectives) } : p)));

  const addPerspective = () => {
    const key = newKey();
    setFocusKey(key);
    setPerspectives((list) => [
      ...list,
      { key, name: "", originalName: null, objectives: [{ key: newKey(), text: "", originalText: null }] },
    ]);
  };

  const addObjective = (perspectiveKey) => {
    const key = newKey();
    setFocusKey(key);
    updateObjectives(perspectiveKey, (list) => [...list, { key, text: "", originalText: null }]);
  };

  const removePerspective = (key) => setPerspectives((list) => list.filter((p) => p.key !== key));

  const discard = () => {
    setSaveError(null);
    setNotice(null);
    applyServerData({ ...JSON.parse(saved), usage });
  };

  // ── Saving ──

  const save = async () => {
    setConfirmImpact(null);
    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      const res = await instance.put(ENDPOINT, payload);
      const data = res.data.data;
      applyServerData(data);
      updateConfig({ Perspective: data.Perspective, Factors: data.Factors });
      setNotice("Saved. New risks will use these perspectives and objectives. Other users see them the next time they open the app.");
    } catch (e) {
      setSaveError(e?.response?.data?.message ?? "Could not save your changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const requestSave = () => {
    if (problem) return;
    if (impact.length) setConfirmImpact(impact);
    else save();
  };

  // ── Render ──

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-200" />)}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {loadError}{" "}
        <button onClick={load} className="font-semibold underline">Try again</button>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {perspectives.map((p, index) => (
          <div key={p.key} className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 rounded-t-xl">
              <input
                value={p.name}
                onChange={(e) => updatePerspective(p.key, { name: e.target.value })}
                placeholder={`Perspective ${index + 1} (e.g. Financial)`}
                aria-label={`Perspective ${index + 1} name`}
                autoFocus={p.key === focusKey}
                className={`${INPUT} font-semibold`}
              />
              <UsagePill count={p.originalName ? usage.perspectives[p.originalName] : 0} />
              <button
                type="button"
                onClick={() => removePerspective(p.key)}
                disabled={perspectives.length === 1}
                title={perspectives.length === 1 ? "At least one perspective is required" : "Remove perspective"}
                aria-label={`Remove perspective ${p.name || index + 1}`}
                className="flex-shrink-0 rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
              >
                <IconRemove />
              </button>
            </div>

            <div className="space-y-2 px-4 py-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Objectives</p>
              {p.objectives.map((o, oi) => (
                <div key={o.key} className="flex items-center gap-3">
                  <input
                    value={o.text}
                    onChange={(e) =>
                      updateObjectives(p.key, (list) => list.map((x) => (x.key === o.key ? { ...x, text: e.target.value } : x)))
                    }
                    placeholder="Strategic objective"
                    aria-label={`Objective ${oi + 1} under ${p.name || `perspective ${index + 1}`}`}
                    autoFocus={o.key === focusKey}
                    className={INPUT}
                  />
                  <UsagePill count={p.originalName && o.originalText ? objectiveUsage(p.originalName, o.originalText) : 0} />
                  <button
                    type="button"
                    onClick={() => updateObjectives(p.key, (list) => list.filter((x) => x.key !== o.key))}
                    disabled={p.objectives.length === 1}
                    title={p.objectives.length === 1 ? "Each perspective needs at least one objective" : "Remove objective"}
                    aria-label={`Remove objective ${oi + 1}`}
                    className="flex-shrink-0 rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                  >
                    <IconRemove />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addObjective(p.key)}
                className="text-xs font-bold text-caribbeanCurrent hover:underline"
              >
                + Add objective
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addPerspective}
        className="mt-4 w-full rounded-xl border-2 border-dashed border-gray-300 py-3 text-sm font-semibold text-gray-500 hover:border-caribbeanCurrent hover:text-caribbeanCurrent"
      >
        + Add perspective
      </button>

      {/* Status + actions */}
      <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
        <div className="mr-auto text-sm" aria-live="polite">
          {dirty && problem && <span className="text-red-600">{problem}</span>}
          {saveError && <span className="text-red-600">{saveError}</span>}
          {!dirty && notice && <span className="text-green-700">{notice}</span>}
        </div>
        <button
          type="button"
          onClick={discard}
          disabled={!dirty || saving}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Discard changes
        </button>
        <button
          type="button"
          onClick={requestSave}
          disabled={!dirty || !!problem || saving}
          className="rounded-lg bg-caribbeanCurrent px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Confirm changes that affect existing risks */}
      {confirmImpact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="perspective-impact-title">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-100 px-5 py-4">
              <h3 id="perspective-impact-title" className="text-base font-bold text-gray-900">These changes affect existing risks</h3>
            </div>
            <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
              <ul className="list-disc space-y-1 pl-5">
                {confirmImpact.map((line) => <li key={line}>{line}</li>)}
              </ul>
              <p className="text-gray-500">
                Existing risks keep the text they were created with. The old names will no longer be offered when entering new risks.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setConfirmImpact(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={save}
                className="rounded-lg bg-caribbeanCurrent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Save anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
